import React from "react";
import { Building2, MapPin, Phone, FileText, Landmark, Copy, UserRound } from "lucide-react";
import { toast } from "sonner";
import { COMPANY } from "@/lib/company";

const L = COMPANY.legal;

const Card = ({ icon: Icon, label, testId, className = "", children }) => (
  <div data-testid={testId} className={`rounded-2xl border border-line bg-surface p-6 md:p-7 ${className}`}>
    <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] font-semibold text-faint mb-4">
      <Icon size={14} strokeWidth={1.8} className="text-brand" /> {label}
    </p>
    <div className="text-sm text-dim leading-relaxed">{children}</div>
  </div>
);

const Row = ({ k, children, testId }) => (
  <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-4 py-2.5 border-b border-line/70 last:border-0">
    <dt className="sm:w-40 shrink-0 text-xs text-faint">{k}</dt>
    <dd className="text-ink font-medium" data-testid={testId}>{children}</dd>
  </div>
);

const copyIban = () => navigator.clipboard.writeText(L.bank.iban.replace(/\s/g, "")).then(() => toast.success("IBAN kopiert"));

const linkCls = "text-ink hover:text-brand transition-colors";

export const ImpressumCards = () => (
  <div className="grid md:grid-cols-2 gap-4 md:gap-5">
    <Card icon={UserRound} label="Medieninhaber & Betreiber" testId="impressum-company" className="md:col-span-2">
      <p className="font-display font-bold text-2xl text-ink tracking-tight">{COMPANY.legalName}</p>
      {COMPANY.owner && <p className="mt-1">{COMPANY.owner} · Geschäftsführer</p>}
    </Card>
    {L.seat && (
      <Card icon={Building2} label="Rechnungsadresse" testId="impressum-billing">
        <p className="text-ink font-medium">{COMPANY.legalName}</p>
        <p>{L.seat.street}<br />{L.seat.city}<br />{COMPANY.country}</p>
      </Card>
    )}
    <Card icon={MapPin} label="Showroom" testId="impressum-showroom">
      <p className="text-ink font-medium">{COMPANY.street}</p>
      <p>{COMPANY.city}<br />{COMPANY.country}</p>
      {COMPANY.appointmentOnly && <p className="text-xs text-faint mt-2">Besuch nach Terminvereinbarung</p>}
    </Card>
    <Card icon={Phone} label="Kontakt" testId="impressum-contact">
      <p>Telefon / WhatsApp: <a href={COMPANY.phoneLink} className={linkCls} data-testid="impressum-phone-link">{COMPANY.phone}</a></p>
      <p>E-Mail: <a href={`mailto:${COMPANY.email}`} className={linkCls} data-testid="impressum-email-link">{COMPANY.email}</a></p>
      {COMPANY.instagram && (
        <p>Instagram: <a href={COMPANY.instagram.url} target="_blank" rel="noopener noreferrer" className={linkCls}
          data-testid="impressum-instagram-link">{COMPANY.instagram.handle}</a></p>
      )}
    </Card>
    <Card icon={FileText} label="Unternehmensdaten" testId="impressum-register">
      <dl>
        {L.purpose && <Row k="Unternehmensgegenstand">{L.purpose}</Row>}
        {L.register && <Row k="Firmenbuchnummer" testId="impressum-fn">{L.register}</Row>}
        {L.court && <Row k="Firmenbuchgericht">{L.court}</Row>}
        {L.vatId && <Row k="UID-Nummer" testId="impressum-uid">{L.vatId}</Row>}
        <Row k="Gewerberecht">Gewerbeordnung: <a href="https://www.ris.bka.gv.at" target="_blank" rel="noopener noreferrer" className="underline hover:text-brand">www.ris.bka.gv.at</a></Row>
      </dl>
    </Card>
    {L.bank && (
      <Card icon={Landmark} label="Bankverbindung" testId="impressum-bank" className="md:col-span-2">
        <dl>
          <Row k="Bank">{L.bank.name}</Row>
          <Row k="IBAN" testId="impressum-iban">
            <span className="inline-flex items-center gap-3 tabular-nums tracking-wider">
              {L.bank.iban}
              <button type="button" onClick={copyIban} aria-label="IBAN kopieren" data-testid="impressum-iban-copy-btn"
                className="inline-flex items-center gap-1 text-xs tracking-normal text-faint hover:text-brand transition-colors">
                <Copy size={13} /> Kopieren
              </button>
            </span>
          </Row>
          {L.bank.bic && <Row k="BIC" testId="impressum-bic"><span className="tracking-wider">{L.bank.bic}</span></Row>}
          <Row k="Kontoinhaber">{COMPANY.legalName}</Row>
        </dl>
      </Card>
    )}
  </div>
);

export default ImpressumCards;
